import React from 'react';
import { Group, Line } from 'react-konva';
import { useMapCanvas } from './map/contexts/MapCanvasContext';
import { getWindowKonvaObject } from './map/helpers/konva/getWindowKonvaObject';
import { StagePosition } from './map/types';

interface GridProps {
  color?: string;
  strokeWidth?: number;
}

export const Grid: React.FC<GridProps> = ({
  color = '#ddd',
  strokeWidth = 1
}) => {
  const { width, height, stagePosition, gridSize: spacing } = useMapCanvas();

  const [position, setPosition] = React.useState<StagePosition>(stagePosition);

  const stageX = position.x;
  const stageY = position.y;
  const scale = position.scale;

  const startX = Math.floor(-stageX / scale / spacing) * spacing;
  const endX = Math.ceil((width - stageX) / scale / spacing) * spacing;
  const startY = Math.floor(-stageY / scale / spacing) * spacing;
  const endY = Math.ceil((height - stageY) / scale / spacing) * spacing;

  const updatePosition = () => {
    const stage = getWindowKonvaObject().stages[0];
    if (stage) {
      setPosition({
        x: stage.x(),
        y: stage.y(),
        scale: stage.scaleX()
      });
    }
  };

  React.useEffect(() => {
    const stage = getWindowKonvaObject().stages[0];
    stage.on('wheel', updatePosition);
    setPosition(stagePosition);

    return () => {
      stage.off('wheel', updatePosition);
    };
  }, [stagePosition.x, stagePosition.y, stagePosition.scale]);

  const { verticalLines, horizontalLines } = React.useMemo(() => {
    const verticalLines: React.ReactNode[] = [];
    const horizontalLines: React.ReactNode[] = [];

    if (position.scale < 1.5) {
      return { verticalLines, horizontalLines };
    }

    for (let x = startX; x < endX; x += spacing) {
      verticalLines.push(
        <Line
          key={`v-${x}`}
          points={[x, startY, x, endY]}
          stroke={color}
          strokeWidth={strokeWidth / scale}
        />
      );
    }

    for (let y = startY; y < endY; y += spacing) {
      horizontalLines.push(
        <Line
          key={`h-${y}`}
          points={[startX, y, endX, y]}
          stroke={color}
          strokeWidth={strokeWidth / scale}
        />
      );
    }

    return { verticalLines, horizontalLines };
  }, [position.x, position.y, position.scale]);

  if (!verticalLines.length || !horizontalLines.length) return null;

  return (
    <Group>
      {verticalLines}
      {horizontalLines}
    </Group>
  );
};
